import { NoData } from "@/components/common/section-components/NoData";
import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { RequestStatusBadge } from "@/components/common/section-components/badge/RequestStatusBadge";
import { PaginationComponent } from "@/components/pagination/PaginationComponent";
import { StandardTable } from "@/components/table/StandardTable";
import { FertSlotRequestActions } from "@/components/common/section-components/action/FertSlotRequestActions";
import { FertSlotRequestFilter } from "@/components/common/section-components/filters/FertSlotRequestFilter";
import { AppRoutes } from "@/constants/routes";
import { SuppliesRequestStatus } from "@/constants/entity-status";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { FieldValues, useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useRoles } from "@/hooks/useRoles";

import {
  clearFiltersHandler,
  fetchFertSlotRequestsHandler,
  submitFiltersHandler,
} from "./schema/handler-functions";
import {
  FertSlotRequestEntity,
  FertSlotRequestListResponse,
} from "./schema/fert-slot-requests.schema";
import { fertSlotRequestsHeaderElements } from "./schema/header-elements";
import { Flex } from "@chakra-ui/react";
import { isEmpty } from "lodash";
import { usePolling } from "@/hooks/usePolling";

export const FertSlotRequestsPage = () => {

  const [isActive, setIsActive] = useState<boolean>(false)

  useEffect(() => {
      setIsActive(true)
  }, [])
  const navigate = useNavigate();
  const { fetchPermissions } = useRoles();

  const { control, handleSubmit, setValue } = useForm();
  const values = useWatch({ control });
  const filtersEmpty = useMemo(
    () => Object.values(values ?? {}).every((v) => isEmpty(v)),
    [values],
  );

  const [openFilters, setOpenFilters] = useState<boolean>(false);
  const [filtersApplied, setFiltersApplied] = useState<boolean>(false);

  const [loading, setLoading] = useState<boolean>(false);
  const [list, setList] = useState<FertSlotRequestListResponse>();
  const [currentFilters, setCurrentFilters] = useState<any>();
  const [page, setPage] = useState<number>(1);

  const fetchList = async (filters?: any, showLoad = true) => {
    await fetchFertSlotRequestsHandler({
      filters,
      showLoad,
      setLoading,
      setList,
      navigate,
      fetchPermissions,
    });
  };

  useEffect(() => {
    if (isActive) {

      fetchList();
    }
  }, [isActive]);

  const submitFilters = async (event: FieldValues) => {
    await submitFiltersHandler({
      event,
      setPage,
      fetchList,
      setCurrentFilters,
      setFiltersApplied,
      setOpenFilters,
      filtersEmpty,
    });
  };

  const clearFilters = async () => {
    await clearFiltersHandler({
      setValue,
      setFiltersApplied,
      setOpenFilters,
      setPage,
      setCurrentFilters,
      fetchList,
    });
  };

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
    fetchList({ ...currentFilters, "P.Pagina": nextPage });
  };

  const handleDetail = (item: FertSlotRequestEntity) => {
    navigate(AppRoutes.FERT_SLOTS_REQ_DETAIL.replace(":id", String(item.id)));
  };

  const getActionElements = (item: FertSlotRequestEntity) => (
    <FertSlotRequestActions handleDetail={handleDetail} item={item} />
  );

  usePolling({
     callback: () => {
      if (!loading) {
        fetchList(
          currentFilters
            ? { ...currentFilters, "P.Pagina": page }
            : { "P.Pagina": page },
          false,
        );
      }
     },
     delay: 12500,
     isActive 
  })

  return (
    <Flex flexDir="column" paddingTop="20px" gap="12px">
      <SectionHeader
        title="Solicitud de cupos de fertilizantes"
        loading={loading}
      />

      <FertSlotRequestFilter
        openFilters={openFilters}
        setOpenFilters={setOpenFilters}
        submitFilters={submitFilters}
        clearFilters={clearFilters}
        control={control}
        handleSubmit={handleSubmit}
        filtersApplied={filtersApplied}
        disableFilters={loading}
        loadingFilters={loading}
        disableApply={filtersEmpty && !filtersApplied}
      />

      <Flex flexDir="column">
        {Boolean(!loading && !list?.listado?.length) && <NoData />}

        {Boolean(!loading && list?.listado?.length) && (
          <>
            <StandardTable
              actionsAlign="center"
              headerLabelAlign="center"
              actionsHeaderLabel="Ver"
              noSelection
              items={
                list?.listado?.map((el: FertSlotRequestEntity) => ({
                  ...el,
                  fechaEntrega: el?.fechaEntrega
                    ? dayjs(el?.fechaEntrega).format("DD/MM/YYYY")
                    : "-",
                  cantidadToneladas: `${new Intl.NumberFormat("es-AR").format(
                    el?.cantidad?? 0,
                  )}`,
                  kmDistancia: `${new Intl.NumberFormat("es-AR").format(
                    el?.kmDistancia ?? 0,
                  )}`,
                  displayEstado: (
                    <RequestStatusBadge
                      status={el?.estadoDescripcion as unknown as SuppliesRequestStatus}
                    />
                  ),
                })) ?? []
              }
              headers={fertSlotRequestsHeaderElements}
              actionElements={getActionElements}
            />
            {Number(list?.totalPages) > 1 && (
              <PaginationComponent
                page={page}
                handlePageChange={handlePageChange}
                pageSize={10}
                count={list?.totalRecords ?? 10}
              />
            )}
          </>
        )}
      </Flex>
    </Flex>
  );
};